import type {ComponentType} from 'react';
import {getTeamRole, type TeamRoleId} from '../src/team';
import {DEMOS} from './demo-catalog';
import {MarketingClosing, MarketingPageIntro} from './marketing';
import {APP_BASE, Link} from './router';
import {teamWorkPath} from './public-paths';

interface WorkType {
  title: string;
  description: string;
}

interface SolutionDefinition {
  path: string;
  label: string;
  title: string;
  description: string;
  tasks: readonly WorkType[];
  workIntroduction: string;
  work: readonly TeamRoleId[];
}

const industrySolutions: readonly SolutionDefinition[] = [
  {
    path: '/solutions/business',
    label: 'Business',
    title: 'Move your business work forward.',
    description: 'Give your team a business question or project brief. Get research, draft content, and a plan you can review.',
    tasks: [
      {title: 'Prepare a decision.', description: 'Compare options against your requirements. Ask for the sources, costs, and open questions before you choose.'},
      {title: 'Plan a project.', description: 'Turn a brief into tasks with owners and dependencies. See which decisions must be made before work can continue.'},
      {title: 'Write for your customers.', description: 'Prepare page content, email drafts, and answers to common questions. Give the team your style and confirmed product details.'},
      {title: 'Build a useful tool.', description: 'Describe a process you want to improve. Ask the team to build a tool and explain how to use it.'},
    ],
    workIntroduction: 'Open the workshop decision pack and budget tool to see completed work, source files, and review notes.',
    work: ['head', 'coder'],
  },
  {
    path: '/solutions/government',
    label: 'Government',
    title: 'Make public information easier to use.',
    description: 'Summarize public reports, compare published data, and prepare clear information for the people who need it.',
    tasks: [
      {title: 'Summarize public reports.', description: 'Give the team the reports and questions to examine. Ask for a summary with sources and a clear list of missing information.'},
      {title: 'Compare published data.', description: 'Check changes across places or years. Ask the team to explain the definitions, calculations, and limits of the comparison.'},
      {title: 'Draft service information.', description: 'Turn confirmed service details into clear instructions. Review the requirements, dates, and contact details before publication.'},
      {title: 'Prepare a delivery plan.', description: 'Set out tasks, dependencies, and review steps. Make the decisions that need an owner easy to find.'},
    ],
    workIntroduction: 'Explore the public World Bank data report and the file handoff that records its source material.',
    work: ['analyst', 'operator'],
  },
  {
    path: '/solutions/customer-support',
    label: 'Customer Support',
    title: 'Prepare clearer customer replies.',
    description: 'Give your team customer questions and approved product information. Get reply drafts and help content for review.',
    tasks: [
      {title: 'Draft a reply.', description: 'Provide the customer’s question and the relevant facts. Ask for a direct answer that follows your support policy.'},
      {title: 'Find repeated questions.', description: 'Review the requests you provide. Group common issues and identify missing help content.'},
      {title: 'Improve help content.', description: 'Turn a confirmed process into clear steps. Ask the team to flag instructions that need more detail.'},
      {title: 'Prepare a handoff.', description: 'Summarize the issue, actions taken, and open questions so the next person can continue the work.'},
    ],
    workIntroduction: 'Read the published copy package for its customer questions and answers. Open the service comparison to see how sources support a recommendation.',
    work: ['writer', 'research'],
  },
  {
    path: '/solutions/legal',
    label: 'Legal',
    title: 'Prepare documents for a closer review.',
    description: 'Compare the documents you provide. Extract key terms and prepare a clear list of questions for your legal reviewer.',
    tasks: [
      {title: 'Compare document versions.', description: 'Identify changes in dates, obligations, and defined terms. Keep each finding linked to the relevant passage.'},
      {title: 'Extract key information.', description: 'Collect names, dates, amounts, and review points in a readable summary. Ask the team to flag missing or unclear text.'},
      {title: 'Organize research.', description: 'Provide the question and sources to examine. Request a source list and separate documented findings from open questions.'},
      {title: 'Prepare review notes.', description: 'Use your templates to draft notes and questions. Keep decisions that need a legal reviewer clearly marked.'},
    ],
    workIntroduction: 'Inspect the research comparison and file handoff to see how the team records sources and organizes material for review.',
    work: ['research', 'operator'],
  },
  {
    path: '/solutions/security',
    label: 'Security',
    title: 'Turn technical findings into clear next steps.',
    description: 'Review code, configuration, and incident notes you provide. Ask the team to explain findings and prepare actions for review.',
    tasks: [
      {title: 'Review code changes.', description: 'Ask the team to inspect input handling, permissions, and error paths. Request file references and an explanation of each finding.'},
      {title: 'Organize incident notes.', description: 'Put the records you provide into a timeline. Separate observed events from assumptions and missing evidence.'},
      {title: 'Prepare response instructions.', description: 'Turn an agreed process into clear steps. Identify the owner, required inputs, and expected result for each action.'},
      {title: 'Check a file handoff.', description: 'List the files that belong in a package. Record their sizes and hashes so the receiving team can check the copies.'},
    ],
    workIntroduction: 'Open the budget tool’s source review and the file-packaging work to inspect their validation rules and verification records.',
    work: ['coder', 'operator'],
  },
];

function PublishedWorkLink({roleId}: {roleId: TeamRoleId}) {
  const demo = DEMOS[roleId];
  if (!demo) throw new Error(`Solutions page references missing public work for ${roleId}`);
  return <article>
    <p className="marketing-label">{getTeamRole(roleId).name} · Public work demo</p>
    <h2>{demo.title}</h2>
    <Link className="marketing-text-link" href={`${APP_BASE}${teamWorkPath(roleId)}`}>Open the work</Link>
  </article>;
}

function SolutionDetail({solution}: {solution: SolutionDefinition}) {
  return <div className="marketing">
    <MarketingPageIntro label={solution.label} title={solution.title}>{solution.description}</MarketingPageIntro>
    <section className="marketing-page-columns" aria-label={`Work for ${solution.label.toLowerCase()}`}>
      {solution.tasks.map(task => <article key={task.title}><h2>{task.title}</h2><p>{task.description}</p></article>)}
    </section>
    <section className="marketing-page-section"><h2>See the work.</h2><p>{solution.workIntroduction}</p></section>
    <div className="marketing-page-columns">{solution.work.map(roleId => <PublishedWorkLink key={roleId} roleId={roleId}/>)}</div>
    <MarketingClosing/>
  </div>;
}

const useCases: readonly {title: string; description: string; work: readonly TeamRoleId[]}[] = [
  {title: 'Research and analysis', description: 'Compare sources, examine data, and explain what the evidence supports.', work: ['research', 'analyst']},
  {title: 'Project planning', description: 'Turn a brief into decisions, tasks, and a plan the team can follow.', work: ['head', 'planner']},
  {title: 'Engineering', description: 'Build a working tool with source code and instructions you can inspect.', work: ['coder']},
  {title: 'Content', description: 'Prepare clear page content, email drafts, and answers to common questions.', work: ['writer']},
  {title: 'Design', description: 'Develop a page layout with content, interaction details, and an implementation handoff.', work: ['designer']},
  {title: 'Operations', description: 'Organize files and prepare a handoff with a record of what is included.', work: ['operator']},
];

export function UseCasesPage() {
  return <div className="marketing">
    <MarketingPageIntro label="Use Cases" title="See what your team can do.">Explore completed work. Open each demo to read the result, inspect the files, and see how it was made.</MarketingPageIntro>
    {useCases.map(useCase => <section key={useCase.title}>
      <div className="marketing-page-section"><h2>{useCase.title}</h2><p>{useCase.description}</p></div>
      <div className="marketing-page-columns">{useCase.work.map(roleId => <PublishedWorkLink key={roleId} roleId={roleId}/>)}</div>
    </section>)}
    <MarketingClosing/>
  </div>;
}

/** The page list supplies the solution routes and navigation labels. */
export const SOLUTION_PAGES: readonly {path: string; label: string; description: string; component: ComponentType}[] = [
  ...industrySolutions.map(solution => ({
    path: solution.path,
    label: solution.label,
    description: solution.description,
    component: () => <SolutionDetail solution={solution}/>,
  })),
  {path: '/use-cases', label: 'Use Cases', description: 'Explore completed research, plans, code, content, design, and file handoffs.', component: UseCasesPage},
];

export function SolutionsPage() {
  return <div className="marketing">
    <MarketingPageIntro label="Solutions" title="Get help with the work you need to do.">Give your team a task and the information it needs. Follow the work and review the result.</MarketingPageIntro>
    <section className="marketing-page-columns" aria-label="Explore solutions">
      {SOLUTION_PAGES.map(solution => <article key={solution.path}>
        <h2>{solution.label}</h2><p>{solution.description}</p>
        <Link className="marketing-text-link" href={`${APP_BASE}${solution.path}`}>Explore {solution.label.toLowerCase()}</Link>
      </article>)}
    </section>
    <MarketingClosing/>
  </div>;
}
